/**
 * Norwegian Bokmål message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: informal du (locked in
 * l10n/locales.md). Uses the `nb` qualifier, never `no`.
 *
 * Platform terms follow Norwegian UI conventions (Tema for theme, samtale
 * for conversation, koble sammen/sammenkobling for pair, avvis for dismiss,
 * mottaker for a receiving surface); "Glance" is the untranslated feature
 * brand name (glossary D1) and "klipp" is the locked D2 loanword in its
 * native form.
 *
 * Plural notes: Norwegian Bokmål is a {one, other} CLDR locale (English-like
 * boundaries), same shape as de/nl. Count nouns inflect (melding → meldinger,
 * enhet → enheter) while neuter monosyllables stay invariant (treff → treff,
 * tegn → tegn, anrop → anrop), so every count-bearing function still selects
 * through `selectPlural("nb", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Tilkoblet",
    statusReconnecting: "Kobler til på nytt…",
    statusDisconnected: "Frakoblet",
    openSettings: "Åpne innstillinger",
    pairedFallbackDevice: "telefonen din",
    publicModeToggleEnable: "Skjul varselinnhold",
    publicModeToggleDisable: "Vis varselinnhold",
  },

  notifications: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Sending mislyktes",
    unread: "Ulest",
    dismiss: "Avvis",
    notificationImageAlt: "Varselbilde",
  },

  conversations: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Sending mislyktes",
    dismiss: "Avvis samtale",
    messagesListLabel: "Meldinger i denne samtalen",
    deviceOwner: "Du",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Ingen uleste meldinger"
        : `${count} ${selectPlural("nb", count, {
            one: "ulest melding",
            other: "uleste meldinger",
          })}`,
  },

  list: {
    today: "I dag",
    yesterday: "I går",
    searchNotifications: "Søk i varsler",
    clearSearch: "Tøm søk",
    clearAll: "Fjern alle",
    markAllRead: "Merk alle som lest",
    groupCheckboxLabel: "Grupper",
    noNotificationsTitle: "Ingen varsler ennå",
    noNotificationsDesc: "Nye meldinger speilet fra telefonen din vises her.",
    noMatchesTitle: "Ingen treff",
    notificationsList: "Varsler",
    privacyAppList: "Varsler per app",
    clear: "Fjern",
    clearMatches: (count: number): string =>
      `Fjern ${count} ${selectPlural("nb", count, { one: "treff", other: "treff" })}`,
    noMatchesDesc: (query: string): string => `Ingen varsler samsvarer med «${query}».`,
    appNotificationsGroup: (appName: string): string => `Varsler fra ${appName}`,
    dismissAllFrom: (appName: string): string => `Avvis alle fra ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("nb", count, { one: "melding", other: "meldinger" })
          : kind === "calls"
            ? selectPlural("nb", count, { one: "anrop", other: "anrop" })
            : selectPlural("nb", count, { one: "varsel", other: "varsler" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "avvis",
    kbdNavigate: "naviger",
    kbdOrSeparator: "eller",
  },

  settings: {
    back: "Tilbake til varsler",
    settings: "Innstillinger",
    general: "Generelt",
    theme: "Tema",
    themeAuto: "Automatisk",
    themeLight: "Lyst",
    themeDark: "Mørkt",
    language: "Språk",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Varsler",
    osNotifications: "Systemvarsler",
    callNotifications: "Anropsvarsler",
    privacySecurity: "Personvern",
    analyticsToggle: "Anonym bruksstatistikk",
    analyticsToggleDescription: "Kun antall og milepæler — aldri varslene dine.",
    statusConnected: "Tilkoblet",
    statusReconnecting: "Kobler til på nytt…",
    statusDisconnected: "Frakoblet",
    disconnectFromPhone: "Koble fra telefonen",
    disconnectConfirm: "Er du sikker? Dette fjerner sammenkoblingen av enheten din.",
    yesDisconnect: "Ja, koble fra",
    cancel: "Avbryt",
    version: "Versjon",
    encryption: "Kryptering",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Trenger du hjelp? Send e-post til __EMAIL__",
    disconnectFailed: "Kunne ikke koble fra — prøv igjen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Koblet fra, men lokal opprydding mislyktes: ${errorMsg}. Last inn siden på nytt.`,
    disconnectLocalCleanupFailed:
      "Koblet fra, men lokal opprydding mislyktes. Last inn siden på nytt.",
  },

  pairing: {
    enterCodeInstruction: "Skriv inn denne koden i Reflecto-appen",
    pairedSuccessfully: "Sammenkobling vellykket!",
    pairingFailed: "Sammenkobling mislyktes",
    requestingPairingCode: "Ber om koblingskode…",
    codeExpired: "Koden er utløpt",
    tryAgain: "Prøv igjen",
    failedToStartPairing: "Kunne ikke starte sammenkobling",
    installHeading: "Har du ikke appen ennå?",
    installScanHint: "Skann med kameraet på telefonen",
    installQrLabel: "QR-kode for å installere Reflecto på telefonen",
    capabilitiesHeading: "Etter sammenkobling kan du:",
    capabilityMirror: "Se varslene fra telefonen her",
    capabilityReply: "Svare uten å ta opp telefonen",
    capabilityShare: "Dele utklippstavle og filer begge veier",
    encryptionNote: "End-to-end-kryptert. Serveren ser aldri innholdet ditt.",
  },

  dnd: {
    title: "Ikke forstyrr",
    description: "Sett varsler på denne enheten på pause den neste timen.",
    snooze1h: "Sett på pause i 1 time",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv til ${formattedTime}`,
    cancelSnooze: "Avbryt",
  },

  connectors: {
    title: "Tilkoblede tjenester",
    emptyBody:
      "Ingen tilkoblede tjenester ennå. Koble til ChatGPT eller Claude fra koblingsinnstillingene deres, og godkjenn deretter koden på telefonen din.",
    scopeSend: "Kan sende varsler",
    added: (date: string): string => `Lagt til ${date}`,
    lastUsed: (relative: string): string => `Sist brukt ${relative}`,
    neverUsed: "Aldri brukt",
    revoke: "Koble fra",
    revokeConfirm: (label: string): string => `Koble fra ${label}? Den mister tilgang umiddelbart.`,
    revokeAction: "Koble fra",
    revokeConfirmLabel: (label: string): string => `Koble fra ${label}`,
    cancel: "Avbryt",
    loadError: "Kunne ikke laste tilkoblede tjenester. Prøv igjen.",
    revokeError: "Kunne ikke koble fra tjenesten. Prøv igjen.",
    retry: "Prøv igjen",
  },

  safetyNumber: {
    rowLabel: "Bekreft kryptering",
    rowSublabel: "Sammenlign sikkerhetstall med telefonen din",
    cardTitle: "Sikkerhetstall",
    caption: (deviceLabel: string): string =>
      `Sammenlign med tallet som vises på ${deviceLabel}. Hvis begge samsvarer, er end-to-end-krypteringen din intakt.`,
    repairNote: "Dette tallet endres når du sammenkobler en enhet på nytt.",
    copy: "Kopier",
    copied: "Kopiert",
    maskedHint: "Skjult mens personvernmodus er på",
  },

  feedback: {
    title: "Send tilbakemelding",
    close: "Lukk",
    messageLabel: "Meldingen din",
    messagePlaceholder: "Funnet en feil, har en idé, eller føles noe galt? Fortell oss det.",
    categoryLabel: "Kategori",
    categoryBug: "Feil",
    categoryIdea: "Idé",
    categoryOther: "Annet",
    emailLabel: "E-post (valgfritt, kun hvis du vil ha svar)",
    emailPlaceholder: "deg@eksempel.no",
    emailHelp: "Vi setter deg ikke på noen liste.",
    disclosure:
      "Meldingen din sendes til Reflectos server og registreres i vår interne feilsporing. Vi legger ved appversjonen din og en enhets-ID tildelt under sammenkobling slik at vi kan korrelere rapporter. Vi ser verken varslene, kontaktene eller andre personopplysninger dine. E-post er valgfritt — oppgi den bare hvis du vil ha svar. Vi setter deg ikke på noen liste.",
    submit: "Send tilbakemelding",
    sending: "Sender…",
    success: "Takk — lest av et menneske, vanligvis innen 24 t",
    error: "Kunne ikke sende. Prøv igjen.",
    done: "Ferdig",
    sendFeedback: "Send tilbakemelding",
  },

  uninstall: {
    headline: "Reflecto-utvidelsen er avinstallert",
    body: "Det fantes aldri noen konto, og utvidelsen lagret sammenkoblingen din på denne datamaskinen, så det er ingenting igjen for deg å rydde opp i.",
    ask: "Har du tretti sekunder, vil vi gjerne vite hva som gikk galt.",
    categoryLegend: "Hva slags tilbakemelding er dette?",
    categoryBug: "Noe var ødelagt",
    categoryIdea: "Det manglet noe jeg trengte",
    categoryOther: "Noe annet",
    messageLabel: "Hva gikk galt?",
    messagePlaceholder: "Varslene sluttet å komme etter noen timer.",
    messageRequired: "Skriv en setning først, og send så.",
    charactersLeft: (count: number): string => `${count} tegn igjen`,
    emailLabel: "E-post (valgfritt)",
    emailPlaceholder: "deg@eksempel.no",
    emailHelp: "Brukes bare til å svare deg. Ingenting annet.",
    submit: "Send tilbakemelding",
    sending: "Sender…",
    retry: "Prøv igjen",
    privacyNote:
      "Dette skjemaet er ikke knyttet til noen enhet. Det inneholder ingen av varslene, utklippstavlen eller filene dine. E-post er valgfritt og brukes bare til å svare.",
    success: "Takk. Et menneske leser hver eneste en.",
    errorGeneric: "Det ble ikke sendt. Meldingen din er fortsatt her.",
    errorRateLimited:
      "Du har sendt flere meldinger i det siste. Prøv igjen om omtrent en time. Teksten din blir stående.",
    footerWebApp: "Reflecto-nettappen fungerer fortsatt uten utvidelsen.",
    footerWebAppLink: "Åpne nettappen",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Lukk",
    aiSummaryTag: "AI-sammendrag",
    errorAi: "Kunne ikke lage et sammendrag akkurat nå.",
    retryCta: "Prøv igjen",
    downloadHeading: "Få AI-sammendrag",
    downloadBody:
      "Ta igjen det du har gått glipp av med et AI-sammendrag på enheten. Kjører helt og holdent på enheten din.",
    downloadCta: "Last ned modell",
    downloadPrivacyNote: "En engangsnedlasting. Serveren ser aldri varslene dine.",
    downloadingHeading: "Laster ned modell…",
    caughtUp: "Du er helt oppdatert — ingenting krever oppmerksomheten din akkurat nå.",
    needsYou: "Krever oppmerksomhet",
    quiet: "Annet",
    scanning: (pending: number): string => `Oppsummerer ${pending}…`,
    jump: "Åpne varsel",
    affordanceReply: "Svar",
    affordanceOpen: "Åpne",
    affordanceCopyOtp: "Kopier kode",
    buttonLabel: "Glance",
  },

  update: {
    title: "Oppdatering tilgjengelig",
    description: "En ny versjon av Reflecto er klar. Last inn på nytt for å oppdatere.",
    reload: "Last inn på nytt",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Vi kalte denne enheten «${name}».`,
    cardLooksGood: "Ser bra ut",
    cardEdit: "Rediger",
    cardAriaLabel: "Bekreft enhetsnavn",
    renameTitle: "Gi denne enheten nytt navn",
    renameLabel: "Enhetsnavn",
    renameSave: "Lagre",
    renameCancel: "Avbryt",
    renameFailed: "Kunne ikke gi nytt navn. Prøv igjen.",
  },

  collision: {
    title: (name: string): string => `Du har allerede en «${name}»`,
    subtitle: "Hva skal vi kalle denne?",
    chipWork: (name: string): string => `Jobb ${name}`,
    chipHome: (name: string): string => `Hjemme ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Egendefinert navn",
    save: "Lagre",
    useAnyway: "Bruk det opprinnelige navnet likevel",
    saveFailed: "Kunne ikke lagre det nye navnet. Prøv igjen.",
    ariaLabel: "Løs duplisert enhetsnavn",
  },

  clipboard: {
    title: "Utklippstavle",
    emptyTitle: "Ingen klipp ennå",
    empty: "Klipp fra de andre enhetene dine dukker opp her. Trykk for å kopiere.",
    from: (label: string): string => `fra ${label}`,
    to: (label: string): string => `til ${label}`,
    copy: "Kopier til utklippstavle",
    copied: "Kopiert",
    reveal: "Vis",
    hide: "Skjul",
    maskedHint: "Innhold skjult — trykk på Vis for å vise",
    sensitiveChip: "Sensitiv",
    cloudChip: "via API-tokenet ditt",
    delete: "Slett klipp",
    sendPlaceholder: "Lim inn tekst for å sende…",
    sendCta: "Send",
    sendSensitiveToggle: "Merk som sensitivt",
    sendSuccess: (n: number): string =>
      `Sendt til ${n} ${selectPlural("nb", n, { one: "enhet", other: "enheter" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Nådde ${n}/${total} ${selectPlural("nb", total, { one: "enhet", other: "enheter" })}.`,
    sendNoDevices: "Ingen sammenkoblede enheter å sende til.",
    sendFailed: "Kunne ikke sende. Prøv igjen.",
    sendEmpty: "Ingenting å sende.",
    dockRecentHeading: "Nylig",
    dockEmptyClipboard: "Utklippstavlen din er tom.",
    clearAll: "Fjern alle",
    clearAllConfirm: "Ja, fjern alle",
    clearAllCancel: "Avbryt",
    shareTitle: "Send til enhetene dine",
    shareReview:
      "Se over hva du sender. Det går til de sammenkoblede enhetene dine, ende-til-ende-kryptert.",
    shareSend: "Send",
    shareCancel: "Avbryt",
    shareNotPaired: "Koble sammen en enhet først for å sende delt tekst.",
    shareOpenApp: "Åpne Reflecto",
    pasteFromClipboard: "Lim inn fra utklippstavlen",
    readFailed: "Kunne ikke lese utklippstavlen — lim inn nedenfor.",
  },
  install: {
    title: "Installer Reflecto",
    body: "Legg til på startskjermen for raskere tilgang.",
    cta: "Installer",
    dismissAria: "Lukk installasjonsforespørsel",
  },
  nav: {
    home: "Hjem",
    clipboard: "Utklippstavle",
    files: "Filer",
    ariaLabel: "Hovednavigasjon",
    fileHiddenPublicMode: "Skjult mens personvernmodus er på.",
  },
  common: {
    loading: "Laster",
  },
  errors: {
    notPaired: "Ikke sammenkoblet",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} på ${os}`,
    webFallback: "Nettleser",
  },

  palette: {
    placeholder: "Skriv en kommando…",
    noResults: "Ingen samsvarende kommandoer",
    ariaLabel: "Kommandopalett",
    clearAll: "Fjern alle varsler",
    glance: "Glance-sammendrag",
    cancelSnooze: "Avbryt pause",
    copyClip: "Kopier siste mottatte klipp",
  },

  fileTransfer: {
    sendTitle: "Send en fil",
    sendChooseFile: "Velg fil",
    sendDeviceLabel: "Send til",
    sendPhoneFallbackName: "Telefon",
    send: "Send fil",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Forbereder…" : `Forbereder — ${pct}%`,
    sendDelivering: "Leverer til telefonen din…",
    sendWaitingAccept: "Venter på at telefonen din godtar…",
    sendWaitingAppOpen: "Åpne Reflecto på telefonen din for å godta",
    sendWaitingAppOpenHint:
      "Telefonen din kan ikke motta push-varsler og viser først denne filen når Reflecto er åpen.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Laster opp del ${partNumber} av ${partCount}…`,
    sendDone: "Sendt.",
    sendFailedRejected: "Telefonen din avslo filen.",
    sendFailedNoAnswer: "Ikke noe svar fra telefonen din.",
    sendFailedUploadFailed: "Opplastingen mislyktes. Prøv igjen.",
    sendFailedCancelled: "Avbrutt.",
    sendFailedGeneric: "Noe gikk galt. Prøv igjen.",
    sendTooLarge: (maxMiB: number): string => `Filen er for stor (maks ${maxMiB} MiB).`,
    sendKeepOpenHint: "Hold denne fanen åpen til overføringen er ferdig.",
    sendAnother: "Send en til",
    sendNoDevice: "Koble sammen en telefon først for å sende filer.",
    sendCancel: "Avbryt",
    sendPeerProgress: (chunksDone: number): string =>
      `Telefonen har mottatt ${chunksDone} blokker.`,

    receiveTitle: "Innkommende filer",
    receiveOfferFrom: (sender: string): string => `${sender} vil sende deg en fil`,
    receiveAccept: "Godta",
    receiveDecline: "Avslå",
    receiveReceiving: (n: number, total: number): string => `Mottar del ${n} av ${total}…`,
    receiveDone: "Lagret.",
    receiveFailed: "Overføringen mislyktes.",
    receiveCancelled: "Avbrutt.",
    receiveCancel: "Avbryt",

    errorChunkAuthFail: "Overføringen mislyktes — dataene kunne ikke verifiseres. Prøv igjen.",
    errorHashMismatch:
      "Overføringen mislyktes — filen besto ikke integritetskontrollen. Prøv igjen.",
    errorStorageFull: "Ikke nok lagringsplass til denne overføringen.",
    errorExpired: "Overføringen er utløpt.",
    errorQuotaExceeded: "Den daglige overføringsgrensen er nådd — prøv igjen i morgen.",
    errorInternal: "Overføringen mislyktes — noe gikk galt.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Lukk",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Overføringsfremdrift",
    outgoing: (n: number): string => `Utgående (${n})`,
    incoming: (n: number): string => `Innkommende (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Godta alle",
    dropTitle: "Dra og slipp her",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Filer opptil ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Dra og slipp her, eller velg en fil å sende",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direkte",
    transportDirectHint: "Sendt direkte mellom enheter — aldri lagret på våre servere.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via server",
    transportRelayHint:
      "Videresendt via våre servere, fortsatt ende-til-ende-kryptert — vi kan ikke lese det.",
    directBadge: "Direkte",
  },
  time: {
    justNow: "akkurat nå",
    minutesAgo: (count: number): string => `${count} min siden`,
    hoursAgo: (count: number): string => `${count} t siden`,
  },
} satisfies MessagesShape;
